import { Controller, Form, FormProvider } from 'react-hook-form';
import { ArrowDropDown } from '@mui/icons-material';
import { t } from 'i18next';
import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { paths } from '@common/types';
import { theme } from '@store';
import {
  CheckboxFormField,
  DatepickerFormField,
  ICD10SearchFormField,
  RadioButtonFormField,
  RepeaterICD10FormField,
  SelectFormField,
  TextFormField,
} from 'components/DynamicFormFields';

import { useCarePlan } from './hooks';
import { FocusConditionFormField } from './components';
import { Dropdown, DropdownMenuItem, SaveChangesModal } from '@components';
import { isNil } from 'lodash';
import { MAXIMUM_TEXT_AREA_LENGTH } from '@common/types/constants.ts';

interface ICarePlanProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  isEditDisabled: boolean;
  tabPanelInstrumentHeight: number;
}

export const CarePlan = ({
  patient,
  isEditDisabled = false,
  tabPanelInstrumentHeight,
}: ICarePlanProps) => {
  const {
    formMethods,
    onSubmit,
    martialStatusOptions,
    chronicConditionsFields,
    handleAddChronicCondition,
    handleRemoveChronicCondition,
    isAddChronicConditionDisabled,
    healthCareGoalsFields,
    handleAddHealthCareGoal,
    handleRemoveHealthCareGoal,
    isAddHealthGoalsDisabled,
    isSubmitting,
    handlePDFDownload,
    lastUpdatedAt,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    fastScoreOptions,
    dementiaTypeOptions,
    isPageDirty,
  } = useCarePlan(patient.id, patient.name);

  const { control, watch, trigger } = formMethods;
  const home_assessment = watch('home_assessment');

  const dropdownMenu: DropdownMenuItem[] = [
    {
      iconName: 'none',
      label: t('patient.carePlan.defaultNotes'),
      onClick: () => handlePDFDownload({ all_notes: false }),
    },
    {
      iconName: 'none',
      label: t('patient.carePlan.allNotes'),
      onClick: () => handlePDFDownload({ all_notes: true }),
    },
  ];

  return (
    <>
      <Stack
        direction={'row'}
        sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
      >
        <Typography variant='subtitle2'>
          {t('patient.carePlan.title')}
        </Typography>
        {lastUpdatedAt && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('patient.carePlan.lastUpdatedOn', {
              date: lastUpdatedAt.format('MMM DD, YYYY'),
            })}
          </Typography>
        )}
      </Stack>
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            padding: '0 16px 16px 16px',
            background: theme.palette.common.white,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 42}px)`,
            overflowX: 'auto',
          }}
        >
          <Box
            sx={{
              paddingY: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              display: 'flex',
              position: 'sticky',
              top: 0,
              backgroundColor: '#fff',
              zIndex: 3,
              mb: 1,
            }}
          >
            <Dropdown
              menuList={dropdownMenu}
              buttonComponent={
                <Button variant='outlined' endIcon={<ArrowDropDown />}>
                  {t('patient.carePlan.downloadPdf')}
                </Button>
              }
            />
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              loading={isSubmitting}
              disabled={isEditDisabled || !isValid || !isPageDirty}
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
            >
              {t('general.save')}
            </Button>
          </Box>
          {chronicConditionsFields.map((conditionEntry, index) => {
            return (
              <Box
                key={conditionEntry.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <FocusConditionFormField
                    name={`chronicConditions.${index}.condition`}
                    actionItemsName={`chronicConditions.${index}.recommendations`}
                    otherConditionTextName={`chronicConditions.${index}.otherConditionText`}
                    placeholder={t('patient.carePlan.condition', {
                      number: index > 0 ? index + 1 : '',
                    })}
                    disabled={isEditDisabled}
                  />

                  <Controller
                    name={`chronicConditions.${index}.recommendations`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth>
                        <TextField
                          {...field}
                          label={t('patient.carePlan.recommendations')}
                          multiline
                          maxRows={4}
                          minRows={4}
                          disabled={isEditDisabled}
                          error={!isNil(error)}
                          helperText={
                            error
                              ? error.message
                              : `${field.value ? field.value.length + `/${MAXIMUM_TEXT_AREA_LENGTH}` : ''}`
                          }
                          slotProps={{
                            formHelperText: {
                              style: {
                                textAlign: error ? 'left' : 'right',
                                margin: 0,
                                padding: '3px 14px 0',
                              },
                            },
                          }}
                        />
                      </FormControl>
                    )}
                  />
                </Box>
                <Button
                  startIcon={<Delete />}
                  sx={{
                    fontWeight: 'regular',
                    fontSize: '1rem',
                    color: theme.palette.error.main,
                    mt: '0.5rem',
                  }}
                  color='error'
                  variant='text'
                  disabled={isEditDisabled}
                  onClick={() => handleRemoveChronicCondition(index)}
                />
              </Box>
            );
          })}

          <Button
            startIcon={<Add />}
            sx={{
              fontWeight: 'regular',
              fontSize: '1rem',
              color: 'primary.main',
              mb: 4,
            }}
            color='secondary'
            variant='text'
            disabled={isAddChronicConditionDisabled || isEditDisabled}
            onClick={handleAddChronicCondition}
          >
            {t('patient.carePlan.add')}
          </Button>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              pb: '1rem',
            }}
          >
            <Typography> Chronic conditions</Typography>
            <RepeaterICD10FormField
              name='chronic_conditions_icd10'
              label='ICD-10 Code or Chronic condition'
              min={2}
              optional={true}
              disabled={isEditDisabled}
            />
          </Box>

          <SelectFormField
            name='fastScore'
            label={t('patient.carePlan.fastScore')}
            options={fastScoreOptions}
            optional
            disabled={isEditDisabled}
          />

          <Typography variant='subtitle2' sx={{ mt: 2, mb: 2 }}>
            {t('patient.carePlan.dementiaDx')}
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <ICD10SearchFormField
              name='dementiaDiagnosis'
              placeholder={t('patient.carePlan.dementiaDiagnosis')}
              disabled={isEditDisabled}
              optional
            />

            <SelectFormField
              name='dementiaType'
              label={t('patient.carePlan.dementiaType')}
              options={dementiaTypeOptions}
              optional
              disabled={isEditDisabled}
            />
          </Box>

          <Typography variant='subtitle2' sx={{ mt: 4, mb: 2 }}>
            {t('patient.carePlan.healthRelatedSocialNeeds')}
          </Typography>
          <SelectFormField
            name='maritalStatus'
            label={t('patient.carePlan.maritalStatus')}
            options={martialStatusOptions}
            optional
            disabled={isEditDisabled}
          />

          <Typography variant='body1' sx={{ mt: 2, mb: 1 }}>
            {t('patient.carePlan.beneficiary')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: 'column',
              pl: 2,
              mb: 2,
            }}
          >
            <CheckboxFormField
              name='hasSteadyAndSafeLivingSituation'
              label={t('patient.carePlan.hasSteadyAndSafeLivingSituation')}
              optional
              disabled={isEditDisabled}
            />
            <CheckboxFormField
              name='hasAccessToMeals'
              optional
              label={t('patient.carePlan.hasAccessToMeals')}
              disabled={isEditDisabled}
            />
            <CheckboxFormField
              name='hasReliableTransportation'
              optional
              label={t('patient.carePlan.hasReliableTransportation')}
              disabled={isEditDisabled}
            />
            <CheckboxFormField
              name='hasReliableUtilities'
              optional
              label={t('patient.carePlan.hasReliableUtilities')}
              disabled={isEditDisabled}
            />
            <CheckboxFormField
              name='isSafeFromViolenceAndAbuse'
              optional
              label={t('patient.carePlan.isSafeFromViolenceAndAbuse')}
              disabled={isEditDisabled}
            />
          </Box>

          <Controller
            name='healthRelatedSocialNeedsNote'
            control={control}
            disabled={isEditDisabled}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ bgcolor: 'white' }}
                type='text'
                size='medium'
                label={t('patient.carePlan.healthRelatedSocialNeedsNote')}
                variant='outlined'
                error={!isNil(error)}
                helperText={
                  error
                    ? error.message
                    : `${field.value ? field.value.length + `/${MAXIMUM_TEXT_AREA_LENGTH}` : ''}`
                }
                fullWidth
                multiline
                minRows={4}
                maxRows={4}
                slotProps={{
                  formHelperText: {
                    style: {
                      textAlign: error ? 'left' : 'right',
                      margin: 0,
                      padding: '3px 14px 0',
                    },
                  },
                }}
              />
            )}
          />

          <Typography variant='subtitle2' sx={{ mt: 4, mb: 2 }}>
            {t('patient.carePlan.healthCareGoals')}
          </Typography>
          {healthCareGoalsFields.map((healthCareGoal, index) => {
            return (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name={`health_care_goals.${index}.goal`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth>
                        <TextField
                          {...field}
                          label={t('patient.carePlan.careGoal', {
                            number: index + 1,
                          })}
                          error={!isNil(error)}
                          helperText={error ? error.message : ''}
                          disabled={isEditDisabled}
                        />
                      </FormControl>
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    alignItems: 'center',
                    display: 'flex',
                    marginTop: '.75rem',
                  }}
                >
                  <Button
                    startIcon={<Delete />}
                    sx={{
                      fontWeight: 'regular',
                      fontSize: '1rem',
                      color: theme.palette.error.main,
                    }}
                    color='error'
                    variant='text'
                    disabled={
                      healthCareGoalsFields.length <= 1 || isEditDisabled
                    }
                    onClick={() => handleRemoveHealthCareGoal(index)}
                  />
                </Box>
              </Box>
            );
          })}
          <Button
            startIcon={<Add />}
            sx={{
              fontWeight: 'regular',
              fontSize: '1rem',
              color: 'primary.main',
            }}
            color='secondary'
            variant='text'
            disabled={isAddHealthGoalsDisabled || isEditDisabled}
            onClick={handleAddHealthCareGoal}
          >
            {t('patient.carePlan.add')}
          </Button>

          <Typography variant='subtitle2' sx={{ mt: 4, mb: 2 }}>
            {t('patient.carePlan.selfManagementRecommendations')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextFormField
              name='recommendation'
              label={t('patient.carePlan.selfManagementRecommendations')}
              optional
              disabled={isEditDisabled}
            />

            <Controller
              name='notes'
              control={control}
              disabled={isEditDisabled}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  sx={{ bgcolor: 'white' }}
                  type='text'
                  size='medium'
                  label={t('patient.carePlan.notes')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={
                    error
                      ? error.message
                      : `${field.value ? field.value.length + `/${MAXIMUM_TEXT_AREA_LENGTH}` : ''}`
                  }
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={4}
                  slotProps={{
                    formHelperText: {
                      style: {
                        textAlign: error ? 'left' : 'right',
                        margin: 0,
                        padding: '3px 14px 0',
                      },
                    },
                  }}
                />
              )}
            />
          </Box>

          <Typography variant='subtitle2' sx={{ mt: 4, mb: 2 }}>
            {t('patient.carePlan.medicalCareCondition')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <TextFormField
                name='physical'
                label={t('patient.carePlan.physical')}
                optional
                disabled={isEditDisabled}
              />
              <Typography
                sx={{ color: 'text.disabled', fontSize: 12, fontWeight: 400 }}
              >
                {t('patient.carePlan.physicalHelper')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextFormField
                name='medical'
                label={t('patient.carePlan.medical')}
                optional
                disabled={isEditDisabled}
              />
              <Typography
                sx={{ color: 'text.disabled', fontSize: 12, fontWeight: 400 }}
              >
                {t('patient.carePlan.medicalHelper')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <TextFormField
                name='psychological'
                label={t('patient.carePlan.psychological')}
                optional
                disabled={isEditDisabled}
              />
              <Typography
                sx={{ color: 'text.disabled', fontSize: 12, fontWeight: 400 }}
              >
                {t('patient.carePlan.psychologicalHelper')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextFormField
                name='other'
                label={t('patient.carePlan.other')}
                optional
                disabled={isEditDisabled}
              />
              <Typography
                sx={{ color: 'text.disabled', fontSize: 12, fontWeight: 400 }}
              >
                {t('patient.carePlan.otherHelper')}
              </Typography>
            </Box>
          </Box>
          <Typography variant='subtitle2' sx={{ mt: 4, mb: 2 }}>
            {t('patient.carePlan.homeAssessment')}
          </Typography>
          <DatepickerFormField
            name='home_assessment_date'
            label={t('patient.carePlan.homeAssessmentDate')}
            disabled={
              home_assessment === 'home_assessment_concerns_not_completed' ||
              isEditDisabled
            }
            clearable
          />
          <RadioButtonFormField
            name='home_assessment'
            disabled={isEditDisabled}
            onChangeCallback={() => trigger('home_assessment_date')}
            options={[
              {
                value: 'no_health_or_safety_concerns_noted',
                label: t('patient.carePlan.no_health_or_safety_concerns_noted'),
              },
              {
                value: 'home_assessment_concerns_noted',
                label: t('patient.carePlan.home_assessment_concerns_noted'),
              },
              {
                value: 'home_assessment_concerns_not_completed',
                label: t(
                  'patient.carePlan.home_assessment_concerns_not_completed',
                ),
              },
            ]}
          />
          <DatepickerFormField
            name='acp_date'
            label={t('patient.carePlan.acp_date')}
            clearable
            disabled={isEditDisabled}
            sx={{ mt: 1 }}
          />
          <SaveChangesModal
            isOpen={isSaveChangesModalOpen}
            onClose={handleCloseSaveChangesModal}
            isValid={isValid}
            handleSaveChanges={handleSaveChanges}
            discardChanges={handleDiscardChanges}
            isLoading={isEditSubmitting}
          />
        </Form>
      </FormProvider>
    </>
  );
};
